import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Grid,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Cancel,
  Edit,
  Delete
} from '@mui/icons-material';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { formatReportMonth } from '../../utils/formatters';
import { useCurrentUser } from '../../hooks/useApi';
import { Navigate } from 'react-router-dom';
import { API_URL } from '../../utils/config';

interface KeyFinding {
  id: number;
  market_id: number;
  market_name?: string;
  report_id: number;
  report_month?: string;
  finding_text: string;
  description?: string;
  display_order: number;
  category?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const KeyFindingDetailAdmin: React.FC = () => {
  const { marketId, keyFindingId } = useParams<{ marketId: string; keyFindingId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedFinding, setEditedFinding] = useState<Partial<KeyFinding>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Redirect if user doesn't have admin or customize permission
  if (!user?.permissions?.includes('market_compass.admin') && !user?.permissions?.includes('market_compass.customize')) {
    return <Navigate to="/" replace />;
  }

  // Fetch key finding details
  const { data: keyFinding, isLoading, error } = useQuery({
    queryKey: ['admin', 'key-finding', keyFindingId],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/key-findings/${keyFindingId}/details`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch key finding');
      }
      const result = await response.json();
      return result.data as KeyFinding;
    }
  });

  // Fetch market details for context
  const { data: market } = useQuery({
    queryKey: ['admin', 'market', marketId],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/markets/all');
      const markets = response.data?.markets || [];
      return markets.find((m: any) => m.id === parseInt(marketId!));
    }
  });

  // Update key finding mutation
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<KeyFinding>) => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/key-findings/${keyFindingId}/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates)
      });
      
      if (!response || !response.ok) {
        const error = await response?.json();
        throw new Error(error?.error || 'Failed to update key finding');
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      setSnackbarMessage('Key finding updated successfully');
      setSnackbarOpen(true);
      setIsEditing(false);
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'key-finding', keyFindingId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'market-key-findings', marketId] });
    },
    onError: (error: any) => {
      setSnackbarMessage(error.message || 'Failed to update key finding');
      setSnackbarOpen(true);
    }
  });

  // Delete key finding mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/marketcompass/admin/key-findings/${keyFindingId}/delete`, {
        method: 'DELETE'
      });
      
      if (!response || !response.ok) {
        const error = await response?.json();
        throw new Error(error?.error || 'Failed to delete key finding');
      }
      
      return response.json();
    },
    onSuccess: () => {
      setSnackbarMessage('Key finding deleted successfully');
      setSnackbarOpen(true);
      // Navigate back to market page after successful deletion
      setTimeout(() => {
        navigate(`/admin/hub/marketcompass/markets/${marketId}`);
      }, 1500);
    },
    onError: (error: any) => {
      setSnackbarMessage(error.message || 'Failed to delete key finding');
      setSnackbarOpen(true);
    }
  });

  const handleEdit = () => {
    setEditedFinding(keyFinding || {});
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedFinding({});
    setIsEditing(false);
  };

  const handleSave = () => {
    updateMutation.mutate(editedFinding);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this key finding? This action cannot be undone.')) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading key finding details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !keyFinding) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ my: 4 }}>
          {error ? 'Failed to load key finding' : 'Key finding not found'}
        </Alert>
      </Container>
    );
  }

  const categories = ['General', 'Market Performance', 'Economic Indicators', 'Industry Trends', 'Investment Outlook'];

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/admin/hub/marketcompass/markets/${marketId}`)}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Key Finding Details
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {market?.name} - {keyFinding.report_month ? formatReportMonth(parseInt(keyFinding.report_month)) : 'No report month'}
            </Typography>
          </Box>
          {!isEditing && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={handleEdit}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                Delete
              </Button>
            </Box>
          )}
        </Box>

        {/* Main Content */}
        <Card>
          <CardContent>
            <Grid container spacing={3}>
              {/* Finding Text */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Key Finding Text
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={editedFinding.finding_text || ''}
                    onChange={(e) => setEditedFinding({ ...editedFinding, finding_text: e.target.value })}
                    variant="outlined"
                  />
                ) : (
                  <Typography variant="h6">
                    {keyFinding.finding_text}
                  </Typography>
                )}
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={editedFinding.description || ''}
                    onChange={(e) => setEditedFinding({ ...editedFinding, description: e.target.value })}
                    variant="outlined"
                    placeholder="Optional detailed description..."
                  />
                ) : (
                  <Typography variant="body1">
                    {keyFinding.description || 'No description provided'}
                  </Typography>
                )}
              </Grid>

              {/* Category and Display Order */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Category
                </Typography>
                {isEditing ? (
                  <FormControl fullWidth>
                    <Select
                      value={editedFinding.category || 'General'}
                      onChange={(e) => setEditedFinding({ ...editedFinding, category: e.target.value })}
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Chip
                    label={keyFinding.category || 'General'}
                    variant="outlined"
                    size="medium"
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Display Order
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    type="number"
                    value={editedFinding.display_order || 0}
                    onChange={(e) => setEditedFinding({ ...editedFinding, display_order: parseInt(e.target.value) })}
                    inputProps={{ min: 0 }}
                  />
                ) : (
                  <Typography variant="body1">
                    {keyFinding.display_order}
                  </Typography>
                )}
              </Grid>

              {/* Status */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                {isEditing ? (
                  <FormControl fullWidth>
                    <Select
                      value={editedFinding.is_active !== false}
                      onChange={(e) => setEditedFinding({ ...editedFinding, is_active: e.target.value === 'true' })}
                    >
                      <MenuItem value="true">Active</MenuItem>
                      <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Chip
                    label={keyFinding.is_active ? 'Active' : 'Inactive'}
                    color={keyFinding.is_active ? 'success' : 'default'}
                    size="medium"
                  />
                )}
              </Grid>

              {/* Metadata */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Created
                      </Typography>
                      <Typography variant="body2">
                        {new Date(keyFinding.created_at).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body2">
                        {new Date(keyFinding.updated_at).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Report Month
                      </Typography>
                      <Typography variant="body2">
                        {keyFinding.report_month ? formatReportMonth(parseInt(keyFinding.report_month)) : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Action Buttons for Edit Mode */}
              {isEditing && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleCancel}
                      disabled={updateMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleSave}
                      disabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};